import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchActivitySummary } from './activityApi';
import {
  ACTIVITY_KIND_LABELS,
  ACTIVITY_PERIOD_LABELS,
  ACTIVITY_PERIODS,
  ActivityKind,
  ActivitySummaryResponse,
} from './activityTypes';
import {
  statsActivityTableSx,
  statsCaptionSx,
  statsCardTitleSx,
  statsCountValueSx,
  statsInteractivePaperSx,
  statsPaperSx,
  statsRatingLabelSx,
  statsRatingValueSx,
  statsSectionStackSx,
} from './statsMuiStyles';

const activityKinds: ActivityKind[] = ['replay', 'puzzles', 'srs'];

export const ActivitySummarySection = ({
  refreshKey = 0,
}: {
  refreshKey?: number;
}) => {
  const [summary, setSummary] = useState<ActivitySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const data = await fetchActivitySummary();
        if (!cancelled) {
          setSummary(data);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Failed to load activity';
          setErrorMessage(message);
          setSummary(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {errorMessage}
      </Alert>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <Box sx={statsSectionStackSx}>
      <Paper variant="outlined" sx={statsPaperSx}>
        <Typography sx={statsRatingLabelSx}>Puzzle rating</Typography>
        <Typography sx={statsRatingValueSx}>
          {Math.round(summary.puzzle_elo)}
        </Typography>
      </Paper>

      {activityKinds.map((kind) => (
        <RouterLink
          key={kind}
          to={`/stats/${kind}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Paper variant="outlined" sx={statsInteractivePaperSx}>
            <Typography sx={statsCardTitleSx}>
              {ACTIVITY_KIND_LABELS[kind]}
            </Typography>
            <Table sx={statsActivityTableSx}>
              <TableHead>
                <TableRow>
                  {ACTIVITY_PERIODS.map((period) => (
                    <TableCell key={period} align="right">
                      {ACTIVITY_PERIOD_LABELS[period]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {ACTIVITY_PERIODS.map((period) => (
                    <TableCell key={period} align="right">
                      <Typography component="span" sx={statsCountValueSx}>
                        {summary[kind][period]}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
            <Typography sx={statsCaptionSx}>
              Click for detailed interaction history
            </Typography>
          </Paper>
        </RouterLink>
      ))}
    </Box>
  );
};

export default ActivitySummarySection;
